import { auth } from '@/api';
import { usePermissionsStore } from '@/stores/permissionsStore';
import { routenames } from './routenames';
import {
  RouteLocation,
  RouteMap,
  RouteRecordNormalized,
  RouteRecordRaw,
  useRouter,
  type RouteLocationNormalizedLoaded,
  type RouteLocationRaw,
  type RouteMeta,
  type Router,
} from 'vue-router';
import type { CheckPermissionMode } from '@/stores/types';
import { RouteLocationNormalized } from 'vue-router';
import { AppPermissionsEnum } from '@/api/generator';

/**
 * Checks whether the logged-in user has all needed permissions for a given RouteLocation.
 */
export function hasRoutePermissions(
  routeName: RouteLocation,
  router: Router,
  permissionsModeDefault: CheckPermissionMode = 'CURRENT_ALL',
) {
  function getPermissions(routeRecord: RouteRecordNormalized) {
    return routeRecord.meta.permissions ?? [];
  }

  function getChildrenPermissions(routeRecord: RouteRecordRaw): AppPermissionsEnum[] {
    return routeRecord.meta?.permissions
      ? routeRecord.meta.permissions
      : (routeRecord.children ?? []).flatMap(getChildrenPermissions);
  }

  const { hasPermissions } = usePermissionsStore();

  const matched = router.resolve(routeName).matched;
  const mode = routeName.meta.permissionsMode ?? permissionsModeDefault;

  switch (mode) {
    case 'CURRENT_ALL':
      return hasPermissions(matched.flatMap(getPermissions), 'ALL');
    case 'CURRENT_ONE':
      return hasPermissions(matched.flatMap(getPermissions), 'ONE');
    case 'CHILDREN_ALL':
      return hasPermissions(matched.flatMap(getChildrenPermissions), 'ALL');
    case 'CHILDREN_ONE':
      return hasPermissions(matched.flatMap(getChildrenPermissions), 'ONE');
  }
}

/**
 * Redirect-Handler for Routes that have multiple potential redirect-routes.
 * The first valid route is returned, otherwise it will route to the "noPermissions"-page
 */
export function handleRedirect(router: Router) {
  return (redirectCandidates: (keyof RouteMap)[]) => {
    return (): RouteLocationRaw => {
      const redirectTarget = redirectCandidates.find((name) => {
        if (auth.isAuthenticated && router.resolve({ name }).meta.noAuthRequired) {
          return false;
        }
        return resolveRouteLocationWithPermissions({ name }, router);
      });

      return { name: redirectTarget ?? routenames.noPermissions };
    };
  };
}

/**
 * Resolves a given location
 * in contrast to router.resolve(), it will also follow redirects and will only return a result if permissions are available
 */
export function resolveRouteLocationWithPermissions(
  location: RouteLocationRaw,
  router: Router,
): RouteRecordNormalized | undefined {
  const resolved = router.resolve(location);
  let target = resolved.matched.at(-1);

  // resolve redirect
  if (target?.redirect) {
    const redirectTarget =
      typeof target.redirect === 'function'
        ? target.redirect(resolved, {} as RouteLocationNormalized)
        : target.redirect;

    target = resolveRouteLocationWithPermissions(redirectTarget, router);
  }

  if (!target) return undefined;
  if (target.name === routenames.noPermissions) return undefined;
  if (hasRoutePermissions(router.resolve(target), router)) {
    return target;
  }
}

/**
 * Read redirect from query-params or return a default-route
 */
export function getSignInRedirect(route: RouteLocation): RouteLocationRaw | undefined {
  // vue-router cannot navigate to href-redirects (like http://...), so we need to strip the origin off
  if (typeof route.query?.redirect === 'string') {
    return route.query.redirect.replace(location.origin, '');
  } else if (route.name === routenames.signIn) {
    return { name: routenames.home };
  } else {
    return undefined;
  }
}

/**
 * Traverse a given route in the router up the tree, to merge all meta data
 */
export function getRouteMetaMerged(route: RouteLocationNormalizedLoaded) {
  return [...route.matched].reduce<RouteMeta>((prev, curr) => ({ ...prev, ...curr.meta }), {});
}

export function useBackUrl(backRouteNames?: symbol[]) {
  const router = useRouter();

  const lastUrl: string | undefined = history.state?.back;

  const hash = lastUrl ? new URL(lastUrl, window.location.origin).hash : undefined;

  const lastMatchedRoutes = lastUrl ? router.resolve(lastUrl).matched : undefined;

  const backRoute = lastMatchedRoutes?.find(
    (route) => route.name && backRouteNames?.includes(route.name as symbol),
  );

  if (backRoute) {
    return { ...backRoute, hash };
  } else {
    return backRouteNames?.[0] ? router.resolve({ name: backRouteNames?.[0], hash }) : undefined;
  }
}

/**
 * Returns a path with this priority:
 * 1. redirect-param set?
 * 2. actually targeting another page? (redirectedFrom)
 * 3. current target
 */
export function getRedirectPathFromRoute(to: RouteLocationNormalized) {
  if (to.query.redirect) {
    return to.query.redirect;
  }
  const target = to.redirectedFrom || to;
  return [routenames.notFound, routenames.signIn, routenames.signOut].includes(
    target.name as symbol,
  )
    ? undefined
    : to.fullPath;
}
